import { Education } from "@/types/supabase";
import { useIsMobile } from "@/hooks/use-mobile";
import { optimizedSupabaseImage } from "@/lib/utils";

interface EducationCardProps {
  education: Education;
  index: number;
  showDescription: boolean;
}

const EducationCard = ({
  education,
  index,
  showDescription,
}: EducationCardProps) => {
  const isMobile = useIsMobile();
  const institutionInitial = (education.institution || education.degree || "?")
    .charAt(0)
    .toUpperCase();

  return (
    <div className="relative mb-8 md:mb-12">
      {/* Timeline dot - adjust for mobile */}
      <div
        className={`absolute ${
          isMobile ? "left-3" : "left-0"
        } w-3 md:w-4 h-3 md:h-4 rounded-full bg-teal transform -translate-x-1/3 z-10`}
      ></div>

      <div
        className={`${isMobile ? "ml-6" : "ml-8"} animate-fade-in`}
        style={{ animationDelay: `${0.2 + index * 0.1}s` }}
      >
        <div className="glass-morphism rounded-xl p-4 md:p-6">
          <div className="flex items-start justify-between gap-4 md:gap-5">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-bold text-green-400 uppercase break-words mb-2">
                {education.degree}
              </h3>

              <div className="bg-gray-800 inline-block px-2 md:px-3 py-1 rounded-sm mb-2">
                <p className="text-xs md:text-sm text-gray-300">
                  {education.period}
                </p>
              </div>

              <p className="text-gray-400 text-sm md:text-base italic">
                {education.institution}
              </p>
            </div>

            <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-gray-900 border border-gray-800 flex items-center justify-center overflow-hidden flex-shrink-0">
              {education.logo_url ? (
                <img
                  src={optimizedSupabaseImage(education.logo_url, 112)}
                  alt={`${education.institution} logo`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width="112"
                  height="112"
                />
              ) : (
                <span className="text-sm md:text-base font-semibold text-gray-300">
                  {institutionInitial}
                </span>
              )}
            </div>
          </div>
          {showDescription && (
            <p className="space-y-2 mb-4">
                  <div className="mt-1.5 mr-2 md:mr-3 h-1.5 w-1.5 rounded-full bg-teal flex-shrink-0"></div>
                  <span className="text-gray-300 text-xs md:text-sm">
                    {education.description}
                  </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EducationCard;
